export interface PendingPfLoan {
    requestDate?: string;
    amountRequested?: number;
    loanReason?: string;
    repaymentTenureMonths?: number;
    monthlyInstallment?: number;
    attachment?: File | null;
}

export interface PendingDeletePfLoan {
    awaitingId?: boolean;
}

export const startCreatePfLoanFlow = (
    setPending: (data: PendingPfLoan | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    setPending({});

    pushAIChat("What is the request date?");
    speak("What is the request date?");
};

export const processPendingPfLoan = (
    text: string,
    pending: PendingPfLoan,
    setPending: (data: PendingPfLoan | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {

    // STEP 1 — REQUEST DATE
    if (!pending.requestDate) {
        const date = new Date(text);

        if (isNaN(date.getTime())) {
            pushAIChat("Please provide a valid request date.");
            speak("Invalid request date.");
            return;
        }

        setPending({
            ...pending,
            requestDate: date.toISOString().split("T")[0]
        });

        pushAIChat("How much loan amount do you need?");
        speak("How much loan amount do you need?");
        return;
    }

    // STEP 2 — AMOUNT
    if (!pending.amountRequested) {
        const amount = Number(text.replace(/[^0-9]/g, ""));

        if (!amount || amount <= 0) {
            pushAIChat("Please provide a valid loan amount.");
            speak("Invalid loan amount.");
            return;
        }

        setPending({ ...pending, amountRequested: amount });

        pushAIChat("What is the loan reason?");
        speak("What is the loan reason?");
        return;
    }

    // STEP 3 — LOAN REASON
    if (!pending.loanReason) {
        setPending({ ...pending, loanReason: text });

        pushAIChat("What is the repayment tenure in months?");
        speak("What is the repayment tenure in months?");
        return;
    }

    // STEP 4 — TENURE
    if (!pending.repaymentTenureMonths) {
        const tenure = Number(text.replace(/[^0-9]/g, ""));

        if (!tenure || tenure <= 0) {
            pushAIChat("Please provide a valid tenure in months.");
            speak("Invalid tenure.");
            return;
        }

        const monthlyInstallment = Number(
            (pending.amountRequested! / tenure).toFixed(2)
        );

        setPending({
            ...pending,
            repaymentTenureMonths: tenure,
            monthlyInstallment
        });

        pushAIChat(
            `Your monthly installment will be ₹${monthlyInstallment}. Do you want to attach any document? You can say skip.`
        );
        speak("Do you want to attach any document?");
        return;
    }

    // STEP 5 — ATTACHMENT (OPTIONAL)
    if (pending.attachment === undefined) {
        if (text.toLowerCase().includes("skip")) {
            finalizePfLoan(pending, setPending, pushAIChat, speak);
            return;
        }

        pushAIChat("Please upload the document now or say skip.");
        speak("Please upload the document or say skip.");
        return;
    }
};

const finalizePfLoan = (
    pending: PendingPfLoan,
    setPending: (data: PendingPfLoan | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    pushAIChat("Creating your PF loan request...");
    speak("Creating your PF loan request.");

    window.dispatchEvent(
        new CustomEvent("ai-create-pf-loan", {
            detail: {
                requestDate: pending.requestDate,
                amountRequested: pending.amountRequested,
                loanReason: pending.loanReason,
                repaymentTenureMonths: pending.repaymentTenureMonths,
                monthlyInstallment: pending.monthlyInstallment,
                attachment: pending.attachment || null
            }
        })
    );

    setPending(null);
};

export const startDeletePfLoanFlow = (
    setPending: (data: PendingDeletePfLoan | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    idFromIntent?: number
) => {
    if (idFromIntent) {
        pushAIChat(`Deleting PF loan request ${idFromIntent}...`);
        speak(`Deleting PF loan request ${idFromIntent}`);

        window.dispatchEvent(
            new CustomEvent("ai-delete-pf-loan", {
                detail: { pfLoanId: idFromIntent }
            })
        );
        return;
    }

    setPending({ awaitingId: true });
    pushAIChat("Please provide the PF loan ID to delete.");
    speak("Please provide the PF loan ID.");
};

export const processPendingDeletePfLoan = (
    text: string,
    pending: PendingDeletePfLoan,
    setPending: (data: PendingDeletePfLoan | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    if (!pending.awaitingId) return;

    const id = Number(text.replace(/[^0-9]/g, ""));

    if (!id) {
        pushAIChat("Please provide a valid PF loan ID.");
        speak("Invalid PF loan ID.");
        return;
    }

    pushAIChat(`Deleting PF loan request ${id}...`);
    speak(`Deleting PF loan request ${id}`);

    window.dispatchEvent(
        new CustomEvent("ai-delete-pf-loan", {
            detail: { pfLoanId: id }
        })
    );

    setPending(null);
};
